// FLOW-13 (SPEC S1, R7; PLAN G3) — o "Usado por" de um segredo do cofre, como a tela o lê.
//
// O servidor manda uma linha por ferramenta e agente (`usado_por: [{tipo, id, nome, agente}]`): o
// flow ligado a dois agentes vem duas vezes. A tela conta e lista FERRAMENTAS, cada uma com os
// agentes dela, e por isso junta as linhas pela ferramenta (`tipo` e `id`, porque o id da legada e o
// da entrada do catálogo são de tabelas diferentes). Só nomes: o valor nunca chega aqui.

// O nome de função é contrato com o modelo (`consultar_pedido`); quem lê a lista quer a frase. A
// mesma regra das cartas da aba Ferramentas do agente (`ToolsSection`, `humanizar`).
export function humanizar(nome) {
  const cru = String(nome || '')
    .replace(/[_-]+/g, ' ')
    .trim();
  return cru ? cru.charAt(0).toUpperCase() + cru.slice(1) : '';
}

const porNome = (a, b) => a.nome.localeCompare(b.nome);

// `[{chave, tipo, id, nome, agentes, agenteId}]`, em ordem de nome, com os agentes sem repetição e em
// ordem. A linha sem agente (o flow que nenhum agente liga) entra sem agente. `agenteId` é o id do
// primeiro agente, em ordem de nome, que veio com id (`agente_id`): é para onde vai o link da tool
// antiga, que mora no agente; nulo quando nenhum veio.
export function ferramentasDoUso(usadoPor) {
  const porChave = new Map();
  (Array.isArray(usadoPor) ? usadoPor : []).forEach(linha => {
    if (!linha || linha.id === undefined || linha.id === null) return;
    const chave = `${linha.tipo}:${linha.id}`;
    if (!porChave.has(chave)) {
      porChave.set(chave, {
        chave,
        tipo: linha.tipo,
        id: linha.id,
        nome: linha.nome || '',
        agentes: [],
        ids: {},
      });
    }
    const ferramenta = porChave.get(chave);
    if (linha.agente && !ferramenta.agentes.includes(linha.agente)) {
      ferramenta.agentes.push(linha.agente);
    }
    if (linha.agente && linha.agente_id != null) {
      ferramenta.ids[linha.agente] ??= linha.agente_id;
    }
  });
  return [...porChave.values()]
    .map(({ ids, ...ferramenta }) => {
      const agentes = [...ferramenta.agentes].sort((a, b) =>
        a.localeCompare(b)
      );
      const comId = agentes.find(agente => ids[agente] != null);
      return { ...ferramenta, agentes, agenteId: comId ? ids[comId] : null };
    })
    .sort(porNome);
}

// As tools antigas (legadas) do uso: só elas esperam o agente publicar de novo para ler o valor novo
// (SPEC S1, "Trocar valor"). O flow e a ferramenta HTTP leem o cofre a cada run.
export function legadasDoUso(usadoPor) {
  return ferramentasDoUso(usadoPor).filter(
    ferramenta => ferramenta.tipo === 'legada'
  );
}

// Os agentes a publicar depois de trocar o valor: os das legadas, sem repetição e em ordem.
export function agentesDasLegadas(usadoPor) {
  const agentes = new Set();
  legadasDoUso(usadoPor).forEach(ferramenta =>
    ferramenta.agentes.forEach(agente => agentes.add(agente))
  );
  return [...agentes].sort((a, b) => a.localeCompare(b));
}

// "Bella", "Bella e Eva", "Bella, Eva e Sayuri": a lista como se fala, no idioma da tela.
export function listaDeNomes(nomes, locale = 'pt-BR') {
  const lista = (nomes || []).filter(Boolean);
  try {
    return new Intl.ListFormat(String(locale).replace('_', '-'), {
      style: 'long',
      type: 'conjunction',
    }).format(lista);
  } catch (e) {
    return lista.join(', ');
  }
}
